import React, { type ComponentType, type ReactElement } from 'react';
import type { Store as ReduxStore } from 'redux';
import {
	createActionsHook,
	createHook,
	createStore,
	createContainer,
	type Action,
	type HookActionsFunction,
} from 'react-sweet-state';
import type { State as IssueState } from '@atlassian/jira-issue-view-common-types/src/issue-type';
import { Provider as ReduxProvider } from '@atlassian/jira-issue-view-react-redux';

/*
 * This sweet state store holds the Issue View redux store.
 *
 * The reason for doing this is because the current version of react-redux jira-frontend
 * is on does not expose the useStore hook and we need to access the redux store further down
 * in the tree so we can wrap issue actions in a Provider. The issue actions are used in the
 * ShortcutsDialog component which is rendered outside of the issue view scope
 * (hence the need to be wrapped in the store provider).
 *
 * Here is the DACI that led to this implementation decision
 * https://hello.atlassian.net/wiki/spaces/JIE/pages/1773458701/Decision+Shortcuts+dialog+rendered+outside+of+issue-view+-+actions+need+access+to+issue-view+redux+store
 *
 * This store should only be used as a short term solution (until the issue view moves to relay).
 * No new usages of this store should be added.
 *
 * DO NOT USE the useStoreForIssueViewShortcutActions_DO_NOT_USE hook
 * OR the withStoreForIssueViewShortcutActions_DO_NOT_USE HOC.
 */

type IssueStore = ReduxStore<IssueState>;
type State = {
	store: null | IssueStore;
};

const actions = {
	setStore:
		(store: null | IssueStore): Action<State> =>
		({ setState }) => {
			setState({ store });
		},
} as const;
type ActionsType = typeof actions;

const Store = createStore<State, ActionsType>({
	initialState: {
		store: null,
	},
	actions,
	name: 'issue-view-store',
});

// eslint-disable-next-line camelcase
const useStore_DO_NOT_USE = createHook(Store);

/* eslint-disable-next-line camelcase */ // @ts-expect-error - TS2322 - Type 'HookActionsFunction<BoundActions<State, { readonly setStore: (store: any) => Action<State, void, void | Promise<void>>; }>>' is not assignable to type 'HookActionsFunction<{ readonly setStore: (store: any) => Action<State, void, void | Promise<void>>; }>'.
export const useStoreActionsForIssueViewShortcutActions_DO_NOT_USE: HookActionsFunction<ActionsType> =
	createActionsHook(Store);

export const StoreForIssueViewContainer = createContainer(Store);

// eslint-disable-next-line camelcase
export const withStoreForIssueViewShortcutActions_DO_NOT_USE =
	<Props,>(WrappedComponent: ComponentType<Props>): ((props: Props) => ReactElement) =>
	// @ts-expect-error - TS2322 - Type '(props: Props) => JSX.Element | null' is not assignable to type '(props: Props) => ReactElement<any, string | JSXElementConstructor<any>>'.
	(props: Props) => {
		const [{ store }] = useStore_DO_NOT_USE();

		return store !== null ? (
			<ReduxProvider store={store}>
				{/* @ts-expect-error - TS2322 - Type 'Props' is not assignable to type 'IntrinsicAttributes & Props & { children?: ReactNode; }'. */}
				<WrappedComponent {...props} />
			</ReduxProvider>
		) : null;
	};
